import React, { useCallback } from 'react';
import styled from 'styled-components';
import TrueImpactText from '../../design-system/base/TrueImpactText';
import {
  faEnvelope,
  faFolderHeart,
  faCalendarDay,
  faDollarSign,
} from '@fortawesome/pro-light-svg-icons';
import { COLORS } from '../../design-system/settings/variables/Colors';
import { SPACERS } from '../../design-system/settings/variables/Spacers';
import {
  FeedbackStatus,
  ReportStage,
} from 'donor/portal/pages/FeedbackDashboard/api';
import CircularIcon from '../../design-system/components/CirclarIcon';
import { formattedDate } from '../../utils/date';
import { numberWithCommas } from '../../utils/numberUtils';
import { useHistory } from 'react-router-dom';
import { Contact } from 'donor/portal/pages/FeedbackDashboard/Feedback/utils';
import { DECORATORS } from '../../design-system/settings/variables/Decorators';
import { ReportStatusModal } from './ReportStatus';
import TrueImpactBadge from 'shared/design-system/components/TrueImpactBadge';
import { FeedbackType } from 'npo/pages/NPOFeedback/SubmitReady/utils';
import { useUserActions } from 'shared/auth/hooks/useUserActions';
import { getReportNameString } from 'npo/pages/ReportsBeta/ReportGroup';
import { ReportingType } from 'npo/pages/ReportsBeta/NewReport/api';

const oneDayInMilliSeconds = 24 * 60 * 60 * 1000;
const notAvailableText = 'Not Available';

const parseContactEmails = (
  emails: string | string[] | undefined,
  isCardClickable: boolean,
): JSX.Element | JSX.Element[] | string => {
  if (!emails) return notAvailableText;
  if (Array.isArray(emails)) {
    return (isCardClickable ? emails.slice(0, 3) : emails).join(', ');
  }
  return emails;
};

const subtractDates = (
  date1: Date | string,
  date2: Date = new Date(),
): string => {
  const days =
    Math.round(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ((new Date(date2) as any) - (new Date(date1) as any)) /
        oneDayInMilliSeconds,
    ) + 1;
  return isNaN(days)
    ? 'Unknown'
    : days < 0
    ? `${days} days`
    : `${days} days past`;
};

const parseContact = (contacts: Contact[]) => {
  const result: {
    trueImpactContact?: Contact;
    donorContact?: Contact;
    npoContact?: Contact;
  } = {};
  contacts.forEach(c => {
    switch (c.accountType) {
      case 'ti staff':
        result.trueImpactContact = c;
        break;
      case 'donor':
        result.donorContact = c;
        break;
      case 'npo':
        result.npoContact = c;
        break;

      default:
        break;
    }
  });
  return result;
};

const onClickStyles = `
  cursor: pointer;
  overflow: hidden;
  transition: all 0.3s;
  border: 1px solid transparent;
  &:hover {
    border-radius: ${SPACERS.tiny};
    border: 1px solid ${COLORS.grayBase};
    box-shadow: ${DECORATORS.boxShadowDefault};
  }
`;
const StyledDiv = styled.a<{
  margin?: string;
  onClick: unknown;
}>`
  background: ${COLORS.greyLight};
  margin-top: 24px;
  ${({ margin }) => (margin ? `margin: ${margin};` : '')}
  display: flex;
  justify-content: space-between;
  border-radius: ${SPACERS.extraTiny};
  text-decoration: none;
  color: inherit;
  &:hover {
    color: inherit;
  }
  ${({ onClick }) => (onClick ? onClickStyles : '')}
  .detail-column {
    width: 100%;
    padding: 30px;
    .heading {
      font-weight: 300;
      font-size: 28px;
      line-height: 30.8px;
      strong {
        font-weight: 700;
      }
    }
    .detail-grid {
      display: grid;
      grid-template-columns: 0.6fr 0.4fr;
      .sub-heading {
        font-size: 18px;
      }
      .donor-details {
        .sub-heading {
          margin-bottom: 16px;
        }
      }
      .npo-details {
        .sub-heading {
          margin-bottom: 16px;
        }
      }
    }
  }
  .bullet-wrapper {
    display: grid;
    grid-template-columns: auto 250px;
    gap: 8px 20px;
  }
  .status-column {
    max-width: 350px;
    padding: 32px;
    text-align: center;
    background-color: ${COLORS.grayLightest};
    .circular-icon {
      margin-bottom: ${SPACERS.tiny};
    }
    .due-in-days {
      margin-bottom: 25px;
    }
    .due-date {
      width: 100%;
      border-radius: 48px;
      padding: ${SPACERS.tiny};
      background-color: ${COLORS.whiteBase};
    }
  }
  .bullet-icon {
    display: grid;
    grid-template-columns: 32px auto;
    .bullet-icon-text {
      margin-top: 4px;
      margin-left: 12px;
      word-break: break-word;
    }
  }
`;

export interface FeedbackDashboardCardProps {
  id: number;
  title: string;
  reportId: number;
  organizationName: string | undefined;
  donorName: string;
  contacts: Contact[];
  lastUpdated: string | undefined;
  dueDate: string;
  stage: ReportStage;
  status: FeedbackStatus;
  year: string | undefined;
  portfolioName: string | undefined;
  totalDonations: number;
  totalCost: number;
  breadcrumb?: React.ReactNode | React.ReactNode[];
  isV3?: boolean;
  accountName: string;
  additionalBudgetNotes: string | null;
  budget: number;
  budgetNote1: string | null;
  budgetNote1Date: string | null;
  budgetNote2: string | null;
  budgetNote2Date: string | null;
  claim: number;
  cnOptedIn: boolean;
  contactDonor: string;
  contactNpo: string[];
  createdAt: string;
  dbId: number;
  directCashVariance: number;
  directInkindVariance: number;
  endDate: string;
  errors: number;
  feedbackType: FeedbackType;
  feedbacks: [];
  fiscalYearEnd: string; // '2023-10-31'
  fiscalYearStart: string; //'2022-11-26'
  indirectCashVariance: number;
  indirectInkindVariance: number;
  investmentType: string | null;
  isAllocated: boolean;
  name: string;
  npoName: string;
  programId: number;
  programName: string;
  relationshipManagerEmail: string | null;
  relationshipManagerFirstName: string | null;
  relationshipManagerId: string | null;
  relationshipManagerLastName: string | null;
  sponsorName: string | null;
  startDate: string;
  tasks: '';
  totalBudgetInitial: number;
  totalBudgetVariance: number;
  updatedAt: string;
  version: 'v4' | null;
  warnings: number;
  reportingType: ReportingType | null;
  optedInFullReporting: boolean | null;
}

const FeedbackDashboardCard: React.FC<FeedbackDashboardCardProps & {
  margin?: string;
  clickable?: true;
  onReportStatusClick?: () => unknown;
}> = ({
  id,
  title,
  organizationName,
  donorName,
  contacts,
  lastUpdated,
  dueDate,
  createdAt,
  status,
  feedbackType,
  reportingType,
  optedInFullReporting,
  year,
  stage,
  portfolioName,
  totalCost = 0,
  totalDonations = 0,
  clickable,
  breadcrumb,
  margin,
  onReportStatusClick,
  isV3 = false,
  cnOptedIn = false,
}) => {
  const { push } = useHistory();
  const { isAdmin } = useUserActions();
  const href = `${window.location.pathname}/${id}`;
  const { trueImpactContact, donorContact, npoContact } = parseContact(
    contacts,
  );

  const onClick = useCallback(() => {
    push(href);
  }, [id, push]);

  return (
    <StyledDiv
      margin={margin}
      href={clickable ? href : undefined}
      onClick={clickable ? onClick : undefined}
    >
      <div className="detail-column">
        {breadcrumb}
        <TrueImpactText className="heading">
          <strong>Report:</strong> {getReportNameString(title)}{' '}
          {isV3 && <TrueImpactBadge>Legacy</TrueImpactBadge>}
          {isAdmin && cnOptedIn && (
            <TrueImpactBadge
              backgroundColor="cnBlue"
              foregroundColor="whiteBase"
            >
              Charity Navigator
            </TrueImpactBadge>
          )}
        </TrueImpactText>
        <div className="detail-grid">
          <div className="donor-details">
            <TrueImpactText variant="body-large" className="sub-heading">
              <strong>Donor:</strong> {donorName || notAvailableText}
            </TrueImpactText>
            <div className="bullet-wrapper">
              <div className="bullet-icon">
                <CircularIcon
                  icon={faFolderHeart}
                  backgroundColor="whiteBase"
                />
                <TrueImpactText variant="body" className="bullet-icon-text">
                  {portfolioName || notAvailableText}
                </TrueImpactText>
              </div>
              <div className="bullet-icon">
                <CircularIcon icon={faDollarSign} backgroundColor="whiteBase" />
                <TrueImpactText variant="body" className="bullet-icon-text">
                  ${numberWithCommas(totalDonations)}
                </TrueImpactText>
              </div>
              <div className="bullet-icon">
                <CircularIcon
                  icon={faCalendarDay}
                  backgroundColor="whiteBase"
                />
                <TrueImpactText variant="body" className="bullet-icon-text">
                  {year || notAvailableText}
                </TrueImpactText>
              </div>
              <div className="bullet-icon">
                <CircularIcon icon={faEnvelope} backgroundColor="whiteBase" />
                <TrueImpactText variant="body" className="bullet-icon-text">
                  {parseContactEmails(donorContact?.email, clickable || false)}
                </TrueImpactText>
              </div>
            </div>
          </div>
          <div className="npo-details">
            <TrueImpactText variant="body-large" className="sub-heading">
              <strong>Grantee:</strong> {organizationName || notAvailableText}
            </TrueImpactText>
            <div className="bullet-icon">
              <CircularIcon icon={faEnvelope} backgroundColor="whiteBase" />
              <TrueImpactText variant="body" className="bullet-icon-text">
                {parseContactEmails(npoContact?.email, clickable || false)}
              </TrueImpactText>
            </div>
          </div>
        </div>
      </div>
      <div className="status-column">
        <CircularIcon
          icon={faEnvelope}
          backgroundColor="whiteBase"
          className="circular-icon"
        />
        <TrueImpactText variant="body-bold">
          True Impact Contact:
        </TrueImpactText>
        <TrueImpactText>
          {trueImpactContact?.email || 'support@trueimpact.com'}
        </TrueImpactText>
        <ReportStatusModal
          dbId={id}
          year={year}
          stage={stage}
          status={status}
          feedbackType={feedbackType}
          reportingType={reportingType}
          optedInFullReporting={optedInFullReporting}
          width={284}
        />
        {/* <div className="due-in-days">
          Time in status: {dueDate ? subtractDates(dueDate) : notAvailableText}
        </div> */}
        <TrueImpactText className="due-date">
          <strong>Due:</strong> {formattedDate(dueDate) || notAvailableText}
        </TrueImpactText>
      </div>
    </StyledDiv>
  );
};

export default React.memo(FeedbackDashboardCard);
